import * as React from "react";
import type { BenchmarkMode } from "./benchmarkMode";
import {
  clearTileHash,
  clampScore,
  gapLabel,
  groupAverages,
  kpiQuestionsForTile,
  readTileHash,
  statusKey,
  writeTileHash,
} from "@/bizminer/lib/heatmap/heatmapUtils";
import type { HeatmapScore, HeatmapGroup } from "@/bizminer/lib/heatmap/heatmapTypes";
import { normalizeTitleKey } from "@/bizminer/lib/heatmap/normalizeTitleKey";
import { round1 } from "@/bizminer/lib/heatmap/scoreMath";
import { kpi_drivers_bizminer_app_pillar_insight_functional_insight_path } from "@/routes";
import type { BizminerInertiaSharedProps } from "@/types/inertia.ts";
import type {
  BizminerHeatmapBenchmarks,
  BizminerHeatmapTaxonomy,
} from "@/types/serializers";

/** Prop types for heatmap Inertia page data — shared with usePage<> in Heatmap.tsx. */
export type HeatmapScoresProp = Record<string, Record<string, HeatmapScore> | undefined>;
export type HeatmapKpiDriverScoresProp = Record<string, Record<string, Record<string, number>> | undefined>;
export type HeatmapKpiMappingProp = Record<string, Record<string, string[]>>;
export type HeatmapGroupScoresProp = Record<string, Record<string, HeatmapScore> | undefined>;
export type HeatmapPillarInsightIdsProp = Record<string, number | undefined>;
export type HeatmapBenchmarksProp = BizminerHeatmapBenchmarks;
type HeatmapBenchmarkValue = NonNullable<BizminerHeatmapBenchmarks["tiles"]>[string] | undefined;

interface UseHeatmapStateParams {
  heatmap_accounts?: BizminerInertiaSharedProps["sidebar"]["accounts"];
  heatmap_scores?: HeatmapScoresProp;
  heatmap_benchmarks?: HeatmapBenchmarksProp;
  heatmap_kpi_mapping?: HeatmapKpiMappingProp;
  heatmap_kpi_driver_scores?: HeatmapKpiDriverScoresProp;
  heatmap_group_scores?: HeatmapGroupScoresProp;
  heatmap_pillar_insight_ids?: HeatmapPillarInsightIdsProp;
  heatmap_taxonomy?: BizminerHeatmapTaxonomy;
  heatmap_selected_account_id?: number;
  heatmap_best_performer_account_id?: number;
  benchmarkMode: BenchmarkMode;
}

function benchmarkValue(
  benchmark: HeatmapBenchmarkValue | undefined,
  mode: BenchmarkMode,
): number | null {
  switch (mode) {
    case "tq":
      return clampScore(benchmark?.tq);
    case "top10":
      return clampScore(benchmark?.top10);
    case "best":
      return clampScore(benchmark?.best);
    default:
      return clampScore(benchmark?.avg);
  }
}

export function useHeatmapState({
  heatmap_accounts,
  heatmap_scores,
  heatmap_benchmarks,
  heatmap_kpi_mapping,
  heatmap_kpi_driver_scores,
  heatmap_group_scores,
  heatmap_pillar_insight_ids,
  heatmap_taxonomy,
  heatmap_selected_account_id,
  heatmap_best_performer_account_id,
  benchmarkMode,
}: UseHeatmapStateParams) {
  const taxonomy = heatmap_taxonomy;
  const gapThreshold = taxonomy?.meta?.gapThreshold ?? 0.1;
  const companyLabel = taxonomy?.meta?.companyLabel ?? "Company";
  const peersLabel = taxonomy?.meta?.peersLabel ?? "Peers avg";
  const taxonomyGroups = taxonomy?.groups ?? [];
  const benchmarkLoading = heatmap_benchmarks == null;

  const accounts = React.useMemo<Array<{ id: number; name: string }>>(() => {
    return (heatmap_accounts || [])
      .filter(
        (
          account,
        ): account is typeof account & { id: number } => typeof account.id === "number",
      )
      .map((account) => ({
        id: account.id,
        name:
          typeof account.name === "string" && account.name.length > 0
            ? account.name
            : `Account #${account.id}`,
      }));
  }, [heatmap_accounts]);

  const accountId = React.useMemo(() => {
    if (
      heatmap_selected_account_id != null &&
      accounts.some((account) => account.id === heatmap_selected_account_id)
    ) {
      return heatmap_selected_account_id;
    }
    return accounts[0]?.id ?? null;
  }, [heatmap_selected_account_id, accounts]);

  const [selectedTileId, setSelectedTileId] = React.useState<string | null>(null);

  const [benchmarkKey, benchmarkValueKey] = React.useMemo(() => {
    switch (benchmarkMode) {
      case "tq":
        return ["peers_tq", "tq"] as const;
      case "top10":
        return ["peers_top10", "top10"] as const;
      case "best":
        return ["peers_best", "best"] as const;
      default:
        return ["peers_avg", "avg"] as const;
    }
  }, [benchmarkMode]);

  const getScore = React.useCallback(
    (accountIdInner: number, tileId: string): HeatmapScore => {
      const raw = heatmap_scores?.[String(accountIdInner)]?.[tileId];
      const benchmark = heatmap_benchmarks?.tiles?.[tileId];

      return {
        company: clampScore(raw?.company),
        peers: clampScore(
          raw?.[benchmarkKey] ??
            raw?.peers ??
            benchmark?.[benchmarkValueKey],
        ),
        peers_avg: clampScore(raw?.peers_avg ?? benchmark?.avg),
        peers_tq: clampScore(raw?.peers_tq ?? benchmark?.tq),
        peers_top10: clampScore(raw?.peers_top10 ?? benchmark?.top10),
        peers_best: clampScore(raw?.peers_best ?? benchmark?.best),
      };
    },
    [benchmarkKey, benchmarkValueKey, heatmap_benchmarks, heatmap_scores],
  );

  const hiddenTileIds = React.useMemo(() => {
    const out = new Set<string>();

    for (const group of taxonomyGroups) {
      for (const tile of group.tiles) {
        const mappedQuestions = kpiQuestionsForTile(
          heatmap_kpi_mapping,
          group.id,
          tile.id,
        );
        if (mappedQuestions.length) continue;

        const hasAnyScore = accounts.some((account) => {
          const raw = heatmap_scores?.[String(account.id)]?.[tile.id];
          const benchmark = heatmap_benchmarks?.tiles?.[tile.id];
          return (
            clampScore(raw?.company) != null ||
            clampScore(benchmark?.avg) != null
          );
        });

        if (!hasAnyScore) out.add(tile.id);
      }
    }

    return out;
  }, [accounts, heatmap_benchmarks, heatmap_kpi_mapping, heatmap_scores, taxonomyGroups]);

  const visibleGroups = React.useMemo(() => {
    return taxonomyGroups
      .map((group) => ({
        ...group,
        tiles: group.tiles.filter((tile) => !hiddenTileIds.has(tile.id)),
      }))
      .filter((group) => group.tiles.length > 0);
  }, [hiddenTileIds, taxonomyGroups]);

  const visibleTileIds = React.useMemo(() => {
    const out = new Set<string>();
    for (const group of visibleGroups) {
      for (const tile of group.tiles) out.add(tile.id);
    }
    return out;
  }, [visibleGroups]);

  const tileVisible = React.useCallback(
    (tileId: string | undefined | null) => (tileId ? visibleTileIds.has(tileId) : false),
    [visibleTileIds],
  );

  React.useEffect(() => {
    function applyTileFromHash() {
      const nextTile = readTileHash();
      setSelectedTileId(tileVisible(nextTile) ? nextTile : null);
    }

    window.addEventListener("hashchange", applyTileFromHash);
    applyTileFromHash();
    return () => window.removeEventListener("hashchange", applyTileFromHash);
  }, [tileVisible]);

  const onSelectTile = (tileId: string) => {
    if (accountId == null) return;
    setSelectedTileId(tileId);
    writeTileHash(tileId);
  };

  const onCloseDrawer = () => {
    setSelectedTileId(null);
    clearTileHash();
  };

  const activeTile = selectedTileId
    ? visibleGroups
        .flatMap((group) => group.tiles.map((tile) => ({ group, tile })))
        .find((entry) => entry.tile.id === selectedTileId)
    : null;

  const activeTileScore =
    selectedTileId && accountId != null ? getScore(accountId, selectedTileId) : null;

  const activeStatus =
    activeTileScore &&
    statusKey(activeTileScore.company, activeTileScore.peers, gapThreshold);
  const activeGap =
    activeTileScore && activeStatus !== "no_data"
      ? gapLabel(activeTileScore.company, activeTileScore.peers)
      : null;

  const kpiQuestions = React.useMemo(() => {
    if (!activeTile) return [];
    return kpiQuestionsForTile(
      heatmap_kpi_mapping,
      activeTile.group.id,
      activeTile.tile.id,
    );
  }, [activeTile, heatmap_kpi_mapping]);

  const points = React.useMemo(() => {
    if (!activeTile || accountId == null) return [];

    const raw = heatmap_kpi_driver_scores?.[String(accountId)]?.[activeTile.tile.id];
    const normalized = new Map<string, number>();

    for (const [title, score] of Object.entries(raw || {})) {
      if (typeof score !== "number") continue;
      normalized.set(normalizeTitleKey(title), score);
    }

    return kpiQuestions.map(
      (question) => normalized.get(normalizeTitleKey(question)) ?? null,
    );
  }, [activeTile, accountId, heatmap_kpi_driver_scores, kpiQuestions]);

  const kpiDriverUrls = React.useMemo(() => {
    if (!activeTile || accountId == null) return [];

    const pillarInsightId = heatmap_pillar_insight_ids?.[String(accountId)];
    if (!pillarInsightId) return [];
    const categoryId = `ai_${activeTile.group.id}`;

    return kpiQuestions.map((question) =>
      kpi_drivers_bizminer_app_pillar_insight_functional_insight_path(
        pillarInsightId,
        categoryId,
        {
          driver: normalizeTitleKey(question).replace(/ /g, "-"),
        },
      ),
    );
  }, [activeTile, accountId, heatmap_pillar_insight_ids, kpiQuestions]);

  const accountTotalScore = React.useCallback(
    (accountIdInner: number): number | null => {
      let sum = 0;
      let count = 0;

      for (const group of taxonomyGroups) {
        const pipelineGroup = heatmap_group_scores?.[String(accountIdInner)]?.[group.id];
        const pipelineCompany = clampScore(pipelineGroup?.company);
        const fallback = groupAverages(accountIdInner, group, getScore).company;
        const value = pipelineCompany ?? fallback;
        if (!Number.isFinite(value)) return null;
        sum += value as number;
        count += 1;
      }

      return count ? round1(sum) : null;
    },
    [getScore, heatmap_group_scores, taxonomyGroups],
  );

  const bestPerformerId = React.useMemo(() => {
    const resolvedBestPerformerId =
      heatmap_best_performer_account_id ??
      heatmap_benchmarks?.best_performer_account_id;

    if (
      resolvedBestPerformerId != null &&
      accounts.some((account) => account.id === resolvedBestPerformerId)
    ) {
      return resolvedBestPerformerId;
    }
    return null;
  }, [accounts, heatmap_best_performer_account_id, heatmap_benchmarks?.best_performer_account_id]);

  const selectedTotal = React.useMemo(() => {
    if (accountId == null) return null;
    return accountTotalScore(accountId);
  }, [accountId, accountTotalScore]);

  const maxTotalScore = taxonomyGroups.length * 5;
  const selectedAccountName =
    accounts.find((account) => account.id === accountId)?.name ?? "Account";

  const getGroupScore = React.useCallback(
    (group: HeatmapGroup) => {
      if (accountId == null) {
        return { avg: { company: null, peers: null }, status: "no_data" as const };
      }

      const pipelineGroup = heatmap_group_scores?.[String(accountId)]?.[group.id];
      const benchmark = heatmap_benchmarks?.groups?.[group.id];

      const pipelineAvg = pipelineGroup
        ? {
            company: clampScore(pipelineGroup.company),
            peers: clampScore(
              pipelineGroup[benchmarkKey] ??
                pipelineGroup.peers ??
                benchmark?.[benchmarkValueKey],
            ),
          }
        : {
            company: null,
            peers: benchmarkValue(benchmark, benchmarkMode),
          };

      const fallbackAvg = groupAverages(accountId, group, getScore);
      const avg = {
        company: pipelineAvg.company ?? fallbackAvg.company,
        peers: pipelineAvg.peers ?? fallbackAvg.peers,
      };

      return { avg, status: statusKey(avg.company, avg.peers, gapThreshold) };
    },
    [
      accountId,
      benchmarkKey,
      benchmarkMode,
      benchmarkValueKey,
      gapThreshold,
      getScore,
      heatmap_benchmarks,
      heatmap_group_scores,
    ],
  );

  return {
    taxonomy,
    gapThreshold,
    companyLabel,
    peersLabel,
    accounts,
    accountId,
    selectedAccountName,
    selectedTileId,
    visibleGroups,
    getScore,
    getGroupScore,
    bestPerformerId,
    benchmarkLoading,
    selectedTotal,
    maxTotalScore,
    activeTile,
    activeTileScore,
    activeStatus,
    activeGap,
    kpiQuestions,
    points,
    kpiDriverUrls,
    onSelectTile,
    onCloseDrawer,
  };
}
